import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { YEUCAU, TRANG_THAI_YEU_CAU_OPTIONS } from '../constants';
import { Get, Post, Put, DeleteSingle, PostFormData } from '../../utils/httpProvider';
import { YEUCAU_API } from '../../utils/apiUrl.js';

// ---------------------------
// Async Thunks
// ---------------------------

// Lấy danh sách Yêu Cầu theo paging
export const yeuCauPagingAction = createAsyncThunk(
    YEUCAU.GET_ALL_PAGING,
    async (model = {}, { rejectWithValue }) => {
        try {
            const pageIndex = model?.PageIndex ?? model?.pageIndex ?? 1;
            const pageSize = model?.PageSize ?? model?.pageSize ?? 20;
            const keyword = model?.Keyword ?? model?.keyword ?? '';

            const params = new URLSearchParams({
                Keyword: keyword,
                PageIndex: pageIndex,
                PageSize: pageSize,
            });

            // helper to append repeated params for arrays or single values
            const appendMulti = (field, variants) => {
                const val = model?.[field];
                if (val === undefined || val === null || val === '') return;
                const arr = Array.isArray(val) ? val : [val];
                arr.forEach(v => {
                    if (v === undefined || v === null || v === '') return;
                    // append under each provided variant name
                    (variants || [field]).forEach(name => params.append(name, v));
                });
            };

            // Lĩnh vực (already supported multi-value)
            if (model?.LinhVucId !== undefined && model?.LinhVucId !== null && model?.LinhVucId !== '') {
                const arrLinhVucId = Array.isArray(model.LinhVucId) ? model.LinhVucId : [model.LinhVucId];
                arrLinhVucId.forEach(id => {
                    if (id) {
                        params.append('LinhVucId', id);
                        params.append('LinhVucIds', id);
                    }
                });
            }

            // LoaiYeuCau: support multi-value
            appendMulti('LoaiYeuCauId', ['LoaiYeuCauId', 'LoaiYeuCauIds']);

            // Status: support multi-value (repeat 'Status')
            appendMulti('Status', ['Status']);

            // KhoaId: support multi-value
            appendMulti('KhoaId', ['KhoaId', 'KhoaIds']);

            // MucDoPhucTap: support multi-value
            appendMulti('MucDoPhucTap', ['MucDoPhucTap']);

            // MucDoUuTien: support multi-value
            appendMulti('MucDoUuTien', ['MucDoUuTien']);

            // NguoiDungId: support multi-value
            appendMulti('NguoiDungId', ['NguoiDungId', 'NguoiDungIds']);

            if (model?.NgayBatDauFrom) params.set('NgayBatDauFrom', model.NgayBatDauFrom);
            if (model?.NgayBatDauTo) params.set('NgayBatDauTo', model.NgayBatDauTo);

            // NhanVienThucHienId: support multi-value
            appendMulti('NhanVienThucHienId', ['NhanVienThucHienId', 'NhanVienThucHienIds']);

            const url = `${YEUCAU_API.GET_ALL_PAGING}?${params.toString()}`;

            const res = await Get(url);
            return res; // fulfilled
        } catch (err) {
            // rejected (để reducer bắt được)
            return rejectWithValue(err);
        }
    }
);


// Lấy 1 Yêu Cầu theo ID
export const yeuCauGetItemAction = createAsyncThunk(
    YEUCAU.GET_BY_ID,
    async (id) => {
        return Get(YEUCAU_API.GET_BY_ID(id)).then(res => res).catch(err => err);
    }
);

// Thêm yêu cầu mới
export const addYeuCauAction = createAsyncThunk(
    YEUCAU.CREATE,
    async (model) => {
        return Post(YEUCAU_API.CREATE, null, model, false)
            .then(res => res)
            .catch(err => { throw err; });
    }
);

// Sửa yêu cầu
export const editYeuCauAction = createAsyncThunk(
    YEUCAU.UPDATE,
    async (model) => {
        return Put(YEUCAU_API.UPDATE(model.id), null, model.data)
            .then(res => res)
            .catch(err => { throw err; });
    }
);

// Xóa yêu cầu
export const deleteYeuCauAction = createAsyncThunk(
    YEUCAU.DELETE,
    async (id, { rejectWithValue, fulfillWithValue }) => {
        try {
            const res = await DeleteSingle(YEUCAU_API.DELETE(id));

            if (res && typeof res.status === 'number') {
                if (res.status >= 200 && res.status < 300) {
                    return fulfillWithValue(res.status);
                }

                let body = null;
                try {
                    body = await res.json();
                } catch (e) {
                    // ignore parse error
                }
                return rejectWithValue(body || { message: 'Xóa dữ liệu thất bại' });
            }

            return rejectWithValue({ message: 'Không nhận được phản hồi từ server' });
        } catch (err) {
            return rejectWithValue(err?.message ? { message: err.message } : err);
        }
    }
);

// FILE ACTIONS
export const yeuCauGetFilesAction = createAsyncThunk(
    `${YEUCAU.NAME_SLICE}/get-files`,
    async (yeuCauId, { rejectWithValue }) => {
        try {
            const res = await Get(YEUCAU_API.GET_FILES_BY_YEUCAU(yeuCauId));
            return res || [];
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const yeuCauAddFileAction = createAsyncThunk(
    `${YEUCAU.NAME_SLICE}/add-file`,
    async ({ yeuCauId, files }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            if (Array.isArray(files)) {
                files.forEach(f => { if (f) formData.append('Files', f); });
            }
            // POST to AddFile/{yeuCauId}
            const url = `${YEUCAU_API.ADD_FILE}/${yeuCauId}`;
            const res = await PostFormData(url, null, formData);
            // backend may return file list or success status
            try {
                const json = await res.json();
                return json;
            } catch {
                return res;
            }
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const yeuCauDeleteFileAction = createAsyncThunk(
    `${YEUCAU.NAME_SLICE}/delete-file`,
    async (fileId, { rejectWithValue }) => {
        try {
            const url = `${YEUCAU_API.DELETE_FILE}/${fileId}`;
            const res = await DeleteSingle(url);
            return res;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

// ---------------------------
// Initial State
// ---------------------------
const initialState = {
    listYeuCau: [],
    totalYeuCau: 0,
    yeuCauItem: null,

    // files
    filesByYeuCau: {},
    loadingFiles: false,

    // ===== paging list loading =====
    loadingPaging: false,
    pagingError: null,
};

// ---------------------------
// Slice
// ---------------------------
export const yeuCauSlice = createSlice({
    name: YEUCAU.NAME_SLICE,
    initialState,
    reducers: {
        // Xóa item khỏi danh sách
        filterListYeuCau: (state, action) => {
            state.listYeuCau = state.listYeuCau.filter(x => x.id !== action.payload);
            state.totalYeuCau -= 1;
        },
        // Lọc theo trạng thái yêu cầu
        filterByTrangThaiYeuCau: (state, action) => {
            const trangThai = action.payload;
            state.listYeuCau = state.listYeuCau.filter(x => x.trangThai === trangThai);
        }
    },
    extraReducers: (builder) => {
        builder
            // ===== paging list =====
            .addCase(yeuCauPagingAction.pending, (state) => {
                state.loadingPaging = true;
                state.pagingError = null;
            })
            .addCase(yeuCauPagingAction.fulfilled, (state, action) => {
                state.loadingPaging = false;
                state.pagingError = null;

                state.listYeuCau = action.payload?.data || [];
                state.totalYeuCau = action.payload?.totalRecords || 0;
            })
            .addCase(yeuCauPagingAction.rejected, (state, action) => {
                state.loadingPaging = false;
                state.pagingError = action.payload || action.error || null;
                // tuỳ bạn: có thể giữ list cũ để UI không “trắng” đột ngột
                // state.listYeuCau = [];
                // state.totalYeuCau = 0;
            })

            // ===== item =====
            .addCase(yeuCauGetItemAction.fulfilled, (state, action) => {
                state.yeuCauItem = action.payload;
            })
            .addCase(yeuCauGetItemAction.rejected, (state) => {
                state.yeuCauItem = null;
            })

            // ===== files =====
            .addCase(yeuCauGetFilesAction.pending, (state) => { state.loadingFiles = true; })
            .addCase(yeuCauGetFilesAction.fulfilled, (state, action) => {
                state.loadingFiles = false;
                const payload = action.payload || [];
                const argId = action?.meta?.arg || null;
                let targetId = argId || null;
                if (!targetId && payload.length > 0) {
                    const p0 = payload[0] || {};
                    targetId = p0.yeuCauId || p0.yeuCauID || p0.YeuCauId || p0.ycId || null;
                }
                if (targetId) state.filesByYeuCau[String(targetId)] = payload;
            })
            .addCase(yeuCauGetFilesAction.rejected, (state) => { state.loadingFiles = false; });
    }
});

// ---------------------------
// Export
// ---------------------------
export const {
    filterListYeuCau,
    filterByTrangThaiYeuCau
} = yeuCauSlice.actions;

export default yeuCauSlice.reducer;

// ---------------------------
// Map label trạng thái Yêu Cầu
// ---------------------------
export const mapTrangThaiYeuCauLabel = (trangThaiYeuCau) => {
    const byValue = TRANG_THAI_YEU_CAU_OPTIONS.find(x => x.value === trangThaiYeuCau);
    if (byValue) return byValue.label;

    const byKey = TRANG_THAI_YEU_CAU_OPTIONS.find(x => x.key === trangThaiYeuCau);
    if (byKey) return byKey.label;

    return trangThaiYeuCau;
};
